/**
 * Inline SVG icons. No icon package: these are a handful of paths, and a
 * dependency would ship a whole font or library to draw four glyphs.
 *
 * Every icon is decorative — the label beside it carries the meaning — so they
 * are hidden from assistive technology rather than given invented alt text.
 */
type IconProps = { size?: number };

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
});

export function ClinicIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M12 9v6M9 12h6" />
    </svg>
  );
}

export function StoreIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M3 9l1.5-5h15L21 9" />
      <path d="M4 9v11h16V9" />
      <path d="M3 9a3 3 0 006 0 3 3 0 006 0 3 3 0 006 0" />
      <path d="M9 20v-6h6v6" />
    </svg>
  );
}

export function UserIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0115 0" />
    </svg>
  );
}

export function ArticleIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v4h4" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  );
}

export function AlertIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v4M12 17.5v.01" />
    </svg>
  );
}

/* --- navigation -----------------------------------------------------------
 * One per destination. A nav where three items share an icon is worse than one
 * with none: the icon stops being a landmark and becomes decoration.
 */

export function DashboardIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </svg>
  );
}

export function ArticleNewIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M19 12.5V7l-4-4H6v18h7" />
      <path d="M15 3v4h4" />
      <path d="M17 16v6M14 19h6" />
    </svg>
  );
}

export function PersonaIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19a6 6 0 0112 0" />
      <path d="M16 6.5a3 3 0 010 6" />
      <path d="M17.5 19a6 6 0 00-2-4.3" />
    </svg>
  );
}

/**
 * The strengths screen: a star, for the four categories of "what this clinic is
 * good at". Drawn on the same 24-unit grid and stroke weight as the rest, so it
 * sits in the nav without redrawing anything around it.
 */
export function StrengthIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8-4.3-4.1 5.9-.9z" />
    </svg>
  );
}

export function TreatmentIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 3v18M3 12h18" />
      <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
    </svg>
  );
}

export function SupervisorIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 3l8 3.5v5c0 5-3.4 8.4-8 9.5-4.6-1.1-8-4.5-8-9.5v-5z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

export function CtaIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M6 4.5l12 6-5 1.7-1.7 5z" />
      <path d="M14 14l5 5" />
    </svg>
  );
}

export function PromptIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 5.5A2.5 2.5 0 016.5 3h11A2.5 2.5 0 0120 5.5v8A2.5 2.5 0 0117.5 16H9l-5 4z" />
      <path d="M8.5 7.5l-2 2 2 2" />
      <path d="M13 7.5l2 2-2 2" />
    </svg>
  );
}

export function LegalIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 3v18" />
      <path d="M6 7h12" />
      <path d="M4 20h16" />
      <path d="M6 7l-2.5 5h5z" />
      <path d="M18 7l-2.5 5h5z" />
    </svg>
  );
}

export function AnalyticsIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M8 20v-6M12.5 20V8M17 20v-9" />
    </svg>
  );
}

export function AuditIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v4h4" />
      <circle cx="11.5" cy="13.5" r="2.8" />
      <path d="M13.7 15.7L16 18" />
    </svg>
  );
}

export function SettingsIcon({ size = 18 }: IconProps) {
  return (
    <svg {...base(size)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M4.2 7.5l2.2 1.3M17.6 15.2l2.2 1.3M4.2 16.5l2.2-1.3M17.6 8.8l2.2-1.3" />
    </svg>
  );
}

export function LogoutIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M14 4.5H6.5A1.5 1.5 0 005 6v12a1.5 1.5 0 001.5 1.5H14" />
      <path d="M17 8.5l3.5 3.5L17 15.5" />
      <path d="M20 12h-9" />
    </svg>
  );
}
